import { useQuery, useMutation } from "@apollo/client";
import { AVAILABILITIES_BY_STUDIO_ID } from "../../graphql/queries/calendarQueries";
import { CREATE_AVAILABILITY, DELETE_AVAILABILITY } from "../../graphql/mutations/calendarMutations";
import { useState, useCallback, useRef, useEffect } from "react";
import { apiDayToCanonicalDay, apiDayToJsDay } from "../../util/studioAvailability";

export const useStudioAvailabilities = (studioId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const refetchTimeoutRef = useRef(null);

  const sanitizeInput = (input = {}) => {
    const cleaned = { ...input };
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === undefined) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  const { 
    data: availabilitiesData, 
    loading: availabilitiesLoading, 
    error: availabilitiesError,
    refetch: originalRefetch
  } = useQuery(AVAILABILITIES_BY_STUDIO_ID, {
    variables: {
      studioID: studioId
    },
    skip: !studioId,
    fetchPolicy: 'cache-and-network'
  });

  // Debounced refetch function
  const debouncedRefetch = useCallback(() => {
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
    }
    
    refetchTimeoutRef.current = setTimeout(() => {
      if (typeof originalRefetch === 'function') {
        originalRefetch();
      }
    }, 500); // 500ms debounce
  }, [originalRefetch]);

  // Mutation to create a new availability
  const [createAvailabilityMutation] = useMutation(CREATE_AVAILABILITY, {
    onCompleted: () => {
      debouncedRefetch();
    },
    onError: (err) => {
      setError(err);
      console.error("Error creating availability:", err);
    }
  });

  // Mutation to delete an availability
  const [deleteAvailabilityMutation] = useMutation(DELETE_AVAILABILITY, {
    onCompleted: () => {
      debouncedRefetch();
    },
    onError: (err) => {
      setError(err);
      console.error("Error deleting availability:", err);
    }
  });

  // Function to create a new availability
  const createAvailability = async (availabilityData = {}) => {
    try {
      setLoading(true);
      setError(null);

      if (!availabilityData?.studioID) {
        throw new Error("studioID is required to create availability");
      }

      const dayOfWeek = apiDayToCanonicalDay(availabilityData.dayOfWeek);
      if (!Number.isInteger(dayOfWeek) || dayOfWeek < 1 || dayOfWeek > 7) {
        throw new Error("dayOfWeek must be a number between 1 and 7");
      }

      if (!availabilityData?.startTime || !availabilityData?.endTime) {
        throw new Error("startTime and endTime are required");
      }

      const input = sanitizeInput({
        studioID: availabilityData.studioID,
        dayOfWeek,
        startTime: availabilityData.startTime,
        endTime: availabilityData.endTime
      });

      const variables = sanitizeInput({
        input,
        condition: availabilityData?.condition
      });

      const result = await createAvailabilityMutation({
        variables
      });

      return result.data?.createAvailability;
    } catch (err) {
      setError(err);
      console.error("Error creating availability:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Function to delete an availability
  const deleteAvailability = async (availability) => {
    try {
      setLoading(true);
      setError(null);

      const id = typeof availability === "string" ? availability : availability?.id;
      if (!id) {
        throw new Error("Availability ID is required");
      }

      const variables = sanitizeInput({
        input: sanitizeInput({ id }),
        condition: typeof availability === "object" ? availability?.condition : undefined
      });

      const result = await deleteAvailabilityMutation({
        variables
      });

      return result.data?.deleteAvailability;
    } catch (err) {
      setError(err);
      console.error("Error deleting availability:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Format availabilities for calendar display
  const formattedAvailabilities = availabilitiesData?.availabilitiesByStudioID?.items?.map(availability => {
    const dayOfWeek = apiDayToCanonicalDay(availability.dayOfWeek);
    const jsDayOfWeek = apiDayToJsDay(availability.dayOfWeek);
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = dayNames[jsDayOfWeek];
    
    // Parse start and end times
    const [startHour, startMinute] = availability.startTime.split(':').map(Number);
    const [endHour, endMinute] = availability.endTime.split(':').map(Number);
    
    return {
      id: availability.id,
      dayOfWeek,
      jsDayOfWeek,
      dayName,
      startTime: availability.startTime,
      endTime: availability.endTime,
      startHour,
      startMinute,
      endHour,
      endMinute
    };
  }) || [];

  // Group availabilities by day of week
  const availabilitiesByDay = formattedAvailabilities.reduce((acc, availability) => {
    if (!acc[availability.dayOfWeek]) {
      acc[availability.dayOfWeek] = [];
    }
    acc[availability.dayOfWeek].push(availability);
    return acc;
  }, {});

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
    };
  }, []);


  return {
    availabilities: formattedAvailabilities,
    availabilitiesByDay,
    loading: loading || availabilitiesLoading,
    error: error || availabilitiesError,
    createAvailability,
    deleteAvailability,
    refetch: debouncedRefetch
  };
};
